import { useContext, useState, useEffect, useCallback } from "react";
import { OrganizationsContext } from "../organizations-context";
import { Typography, Dialog, DialogContent, DialogTitle } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit'
import { useTranslation } from "react-i18next";
import { StyledButton } from "../../../components/styled-button/styled-button";
import { useService } from "../organizations-service";
import pubSub, { EVENTS } from "../../../config/pubSub";
import { IAddEditProviderModel } from "../organization-upsert-form";
import EditComponent from "./edit-component";
import { styled } from '@mui/material/styles';
import { IOrganizationModel } from "../organizations.model";
import { StyledDialogContent, StyledDialogTitle, StyledTypography } from "../../../components/shared-styles/dialog-title-styles";

export const EditOrganizationComponent = () => {
  const [openEditOrganization, setOpenEditOrganization] = useState<boolean>(false);
  const { orgService } = useService();
  const [isAllowAddChild, setIsAllowAddChild] = useState<boolean>(false);
  const { isOrgListLoading, selectedOrg, setSelectedOrg, setSelectedCheckedOrgs } = useContext(OrganizationsContext);
 
  const { t } = useTranslation();

  const checkOrgLimit = useCallback(async () => {
    try {
      if (!orgService.getOrgLimit) return;
      const res: any = await orgService.getOrgLimit();
      setIsAllowAddChild(res.childrenAdd);
    } catch (error) {
      pubSub.publish(EVENTS.ERROR, "common_error_msg");
    }
  }, [orgService]);

  useEffect(() => {
    checkOrgLimit();
  }, [checkOrgLimit]);

  const handleEditClickOpen = () => {
    setOpenEditOrganization(true);
  };

  const handleCloseEdit = () => {
    setOpenEditOrganization(false);
    setSelectedOrg(null);
    setSelectedCheckedOrgs([]);
  }

  const handleEditDone = () => {
    setSelectedOrg(null);
    setOpenEditOrganization(false);
    pubSub.publish(EVENTS.ORGANIZATIONS_LIST);
  };

  return (
    <div>
      <StyledButton
        variant="contained"
        onClick={handleEditClickOpen}
        className="d-flex align-items-center"
        disabled={!selectedOrg || !isAllowAddChild || isOrgListLoading}
        startIcon={<EditIcon />}
      >
        {t("cmp_organization_edit_button")}
      </StyledButton>
      <EditOrganizationDialog
        open={openEditOrganization} 
        selectedOrg={selectedOrg}
        handleEditOrganizationClose={handleCloseEdit}
        handleEditOrganizationDone={handleEditDone}
      />
    </div>
  );
};

type IEditOrganizationProps = {
  open: boolean;
  selectedOrg: string | null;
  handleEditOrganizationClose: () => void;
  handleEditOrganizationDone: () => void;
};

export const EditOrganizationDialog = (props: IEditOrganizationProps) => {
  const { t } = useTranslation();
  const { selectedOrg } = useContext(OrganizationsContext);
  const { orgService } = useService(); // Get orgService
  const [loading, setLoading] = useState(true); // Add loading state
  const [initialEmail, setInitialEmail] = useState<string>("");

  const [formModel, setFormModel] = useState<IAddEditProviderModel>({
    0: {
      name: "",
      displayName: "",
      email: "",
      url: ""
    },
    isValid: false,
    hasChanges: false, 
  });
  
  const [hasChanges, setHasChanges] = useState<boolean>(false); 

  const handleClose = useCallback(() => {
    props.handleEditOrganizationClose();
  }, [props.handleEditOrganizationClose]);

  const handleModelChange = (updatedModel: any) => {
    setFormModel(updatedModel);
  };

  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after component unmounts
     
    const fetchAndSetOrgData = async () => {
      if (!selectedOrg || !orgService.getOrg) return; // Handle undefined values gracefully

      setLoading(true);

      try {
        const org: IOrganizationModel = await orgService.getOrg(selectedOrg); // Fetch org data

        if (isMounted) { // Only update state if component is still mounted
          const updatedModel = {
              0: {
                name: org.name,
                displayName: org.displayName,
                email: org.email,
                url: org.url,
                locale: org.locale,
              },
              isValid: false,
              isActivated: org?.isActivated
          };
          setFormModel(updatedModel);
          setInitialEmail(org.email);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (props.open) {
    fetchAndSetOrgData();
    }

    return () => {
      isMounted = false; // Set flag to false on unmount
    };
}, [selectedOrg, orgService, props.open]);

  return (
    <Dialog
      open={props.open}
      maxWidth="sm"
      fullWidth={true}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      disableEscapeKeyDown
      aria-labelledby="form-dialog-title"
    >
      <StyledDialogTitle id="form-dialog-title">
        <StyledTypography>{t("cmp_organization_edit_button")}</StyledTypography>
      </StyledDialogTitle>
      <StyledDialogContent>
        <EditComponent
            handleDone={() => {
              handleClose();
              props.handleEditOrganizationDone();
            }}
            handleClose={handleClose}
            model={formModel}
            handleModelChange={handleModelChange}
            initialEmail={initialEmail}
            hasChanges={hasChanges}
            setHasChanges={setHasChanges}
        />
      </StyledDialogContent>
    </Dialog>
  );
};

export default EditOrganizationComponent;
